import { useEffect, useState } from "react";
import API from "../lib/api";
import { Button } from "./ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Badge } from "./ui/badge";
import { UserCircle, X as XIcon, Plus } from "@phosphor-icons/react";
import { toast } from "sonner";

/**
 * Multi-operative manager for a task.
 *
 * Modes:
 *   - compact (default false): full UI with add/remove and "+ Add" affordance
 *   - compact=true: read-only chip list (used inline on task rows / kanban cards)
 *
 * Props:
 *   - taskId         — the task id (drives /api/tasks/{id}/assign-operative endpoints)
 *   - operatives     — current `assigned_operatives` array on the task ([{user_id, name}])
 *   - team           — full in-house team list ([{_id, name, email}]) — only needed when not compact
 *   - canEdit        — bool, whether to show add/remove controls
 *   - onChange       — (newList) => void, called after a successful add/remove
 *   - compact        — bool
 *   - testIdPrefix   — default "task-operatives"
 */
export default function TaskOperativeChips({
  taskId, operatives = [], team = [], canEdit = false, onChange, compact = false, testIdPrefix = "task-operatives",
}) {
  const [adding, setAdding] = useState(false);
  const [picked, setPicked] = useState("");
  const [working, setWorking] = useState(false);

  const handleAdd = async () => {
    if (!picked) return;
    setWorking(true);
    try {
      const { data } = await API.post(`/tasks/${taskId}/assign-operative`, { operative_id: picked });
      onChange && onChange(data.operatives || []);
      setPicked("");
      setAdding(false);
      toast.success("Operative added");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to add operative");
    } finally {
      setWorking(false);
    }
  };

  const handleRemove = async (operativeId) => {
    setWorking(true);
    try {
      await API.delete(`/tasks/${taskId}/assign-operative/${operativeId}`);
      onChange && onChange((operatives || []).filter(o => o.user_id !== operativeId));
      toast.success("Operative removed");
    } catch {
      toast.error("Failed to remove operative");
    } finally {
      setWorking(false);
    }
  };

  // ---- Compact (read-only) inline chips ----
  if (compact) {
    if (!operatives?.length) return null;
    return (
      <div className="flex flex-wrap gap-1" data-testid={`${testIdPrefix}-compact`}>
        {operatives.slice(0, 3).map(op => (
          <span
            key={op.user_id}
            className="inline-flex items-center gap-1 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-full px-1.5 py-0.5 text-[10px] font-medium"
            title={op.name}
          >
            <UserCircle size={10} className="text-amber-600" />
            <span className="truncate max-w-[80px]">{op.name}</span>
          </span>
        ))}
        {operatives.length > 3 && (
          <Badge variant="secondary" className="text-[9px]">+{operatives.length - 3}</Badge>
        )}
      </div>
    );
  }

  // ---- Full controls ----
  const available = team.filter(u => !operatives.some(op => op.user_id === u._id));

  return (
    <div className="space-y-2" data-testid={`${testIdPrefix}-full`}>
      <div className="flex flex-wrap gap-2">
        {operatives.length === 0 ? (
          <p className="text-xs text-muted-foreground italic">No operatives on this task yet.</p>
        ) : operatives.map(op => (
          <div key={op.user_id} className="inline-flex items-center gap-2 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-full pl-2.5 pr-1 py-0.5 text-xs">
            <UserCircle size={12} className="text-amber-600" />
            <span className="font-medium" data-testid={`${testIdPrefix}-${op.user_id}`}>{op.name}</span>
            {canEdit && (
              <button
                onClick={() => handleRemove(op.user_id)}
                disabled={working}
                className="rounded-full hover:bg-red-100 dark:hover:bg-red-900/40 p-1 text-red-600 transition-colors disabled:opacity-50"
                title="Remove from task"
                data-testid={`${testIdPrefix}-remove-${op.user_id}`}
              >
                <XIcon size={10} />
              </button>
            )}
          </div>
        ))}
      </div>

      {canEdit && !adding && (
        <Button
          variant="outline"
          size="sm"
          className="h-7 text-xs"
          onClick={() => setAdding(true)}
          disabled={available.length === 0}
          data-testid={`${testIdPrefix}-add-btn`}
        >
          <Plus size={12} className="mr-1" /> Add operative
        </Button>
      )}

      {canEdit && adding && (
        <div className="flex items-center gap-2 flex-wrap">
          <Select value={picked} onValueChange={setPicked}>
            <SelectTrigger className="h-8 text-xs flex-1 min-w-[160px]" data-testid={`${testIdPrefix}-pick-select`}>
              <SelectValue placeholder="Choose operative…" />
            </SelectTrigger>
            <SelectContent>
              {available.length === 0 ? (
                <div className="px-2 py-2 text-[11px] text-muted-foreground">All operatives are already on this task.</div>
              ) : available.map(u => (
                <SelectItem key={u._id} value={u._id}>{u.name || u.email}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button size="sm" className="h-8" onClick={handleAdd} disabled={!picked || working} data-testid={`${testIdPrefix}-confirm-add`}>Add</Button>
          <Button size="sm" variant="ghost" className="h-8" onClick={() => { setAdding(false); setPicked(""); }}>Cancel</Button>
        </div>
      )}
    </div>
  );
}

// Re-fetch helper for callers that need it after an add/remove from elsewhere.
export async function fetchTaskOperatives(taskId) {
  try {
    const { data } = await API.get(`/tasks/${taskId}/operatives`);
    return data.operatives || [];
  } catch {
    return [];
  }
}
